import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ClipboardList, Download, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import { deleteFoodLog, listFoodLogs } from "../lib/v2Database";
import { exportV2HistoryPdf } from "../lib/v2PdfReport";
import {
  calculateDailyTotals,
  formatLogDate,
  formatLogTime,
  getLogListedServing,
  getLogPortions,
  getLogsForDate,
  groupLogsByDate,
  sortLogsNewestFirst,
} from "../lib/v2FoodLogUtils";
import { deleteV2FoodLog, loadV2FoodLogs } from "../lib/v2Storage";
import { formatServing } from "../lib/v2FoodUtils";

export default function V2History() {
  const { lang } = useLang();
  const { isGuest, isRegistered, settings, user } = useV2Session();
  const copy = getV2Copy(lang);
  const historyCopy = copy.historyCopy;
  const foodsCopy = copy.foodsCopy;
  const [logs, setLogs] = useState(() => (isRegistered ? [] : loadV2FoodLogs({ isGuest })));
  const [selectedDate, setSelectedDate] = useState(() => formatLogDate());
  const [search, setSearch] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const selectedLogs = useMemo(
    () => {
      const query = search.trim().toLowerCase();
      const dayLogs = getLogsForDate(logs, selectedDate);
      const filtered = query
        ? dayLogs.filter((log) => String(log.food_name_snapshot || "").toLowerCase().includes(query))
        : dayLogs;
      return sortLogsNewestFirst(filtered);
    },
    [logs, search, selectedDate]
  );
  const totals = useMemo(() => calculateDailyTotals(selectedLogs), [selectedLogs]);
  const groupedDates = useMemo(
    () => Object.keys(groupLogsByDate(logs)).sort((a, b) => b.localeCompare(a)),
    [logs]
  );
  const todayLogs = useMemo(() => getLogsForDate(logs, formatLogDate()), [logs]);

  useEffect(() => {
    let alive = true;

    async function loadHistory() {
      if (isRegistered && user?.id) {
        setLoadingHistory(true);
        setHistoryError("");
        const loadedLogs = await listFoodLogs(user.id).catch(() => null);
        if (!alive) return;
        if (loadedLogs) {
          setLogs(loadedLogs);
        } else {
          setHistoryError(historyCopy.dataError);
        }
        setLoadingHistory(false);
        return;
      }

      setLogs(loadV2FoodLogs({ isGuest }));
      setHistoryError("");
      setLoadingHistory(false);
    }

    loadHistory();
    return () => {
      alive = false;
    };
  }, [historyCopy.dataError, isGuest, isRegistered, user?.id]);

  const handleDelete = async (log) => {
    const confirmed = window.confirm(formatCopy(historyCopy.deleteConfirm, { name: log.food_name_snapshot || "Food" }));
    if (!confirmed) return;

    const result =
      isRegistered && user?.id
        ? await deleteFoodLog(user.id, log.id).catch(() => ({ ok: false, deletedId: null }))
        : deleteV2FoodLog(log.id, { isGuest });
    if (!result.ok) {
      toast.error(historyCopy.deleteError);
      return;
    }

    if (isRegistered && user?.id) {
      setLogs((current) => current.filter((item) => item.id !== log.id));
    } else {
      setLogs(loadV2FoodLogs({ isGuest }));
    }
    toast.success(historyCopy.deleteSuccess);
  };

  const handleExportPdf = (days, selectedDateOverride) => {
    exportV2HistoryPdf({
      logs,
      days,
      selectedDate: selectedDateOverride,
      profile: settings?.profile || {},
    });
  };

  return (
    <section className="mx-auto max-w-7xl min-w-0 space-y-6 overflow-x-hidden">
      <header className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div className="min-w-0 space-y-2">
          <p className="text-sm font-semibold text-primary">{copy.brand}</p>
          <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">{historyCopy.title}</h1>
          <p className="max-w-3xl text-muted-foreground">{historyCopy.description}</p>
        </div>
        <div className="grid min-w-0 gap-2 sm:flex sm:flex-wrap sm:justify-end">
          <Button type="button" variant="outline" className="justify-start sm:justify-center" onClick={() => handleExportPdf(1, selectedDate)}>
            <Download className="h-4 w-4" />
            {historyCopy.selectedDayPdf}
          </Button>
          <Button type="button" variant="outline" className="justify-start sm:justify-center" onClick={() => handleExportPdf(14)}>
            <Download className="h-4 w-4" />
            {historyCopy.fourteenDayPdf}
          </Button>
          <Button type="button" variant="outline" className="justify-start sm:justify-center" onClick={() => handleExportPdf(30)}>
            <Download className="h-4 w-4" />
            {historyCopy.thirtyDayPdf}
          </Button>
          <Button asChild className="justify-start sm:justify-center">
            <Link to="/food-logging">
              <Plus className="h-4 w-4" />
              {historyCopy.logFood}
            </Link>
          </Button>
        </div>
      </header>

      {historyError && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          {historyError}
        </section>
      )}

      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <section className="min-w-0 space-y-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <CalendarDays className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold">{historyCopy.selectDate}</h2>
                <p className="text-sm text-muted-foreground">{historyCopy.chooseDay}</p>
              </div>
            </div>
            <Input
              className="mt-4"
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
            />
          </div>

          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-bold">{historyCopy.todaysLogs}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{formatCopy(historyCopy.todayCount, { count: todayLogs.length })}</p>
          </section>

          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-bold">{historyCopy.daysWithHistory}</h2>
            {groupedDates.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {groupedDates.map((dateKey) => (
                  <Button
                    key={dateKey}
                    type="button"
                    variant={selectedDate === dateKey ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedDate(dateKey)}
                  >
                    {dateKey}
                  </Button>
                ))}
              </div>
            ) : (
              <p className="mt-3 rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                {loadingHistory ? historyCopy.loading : historyCopy.noFoodLoggedYet}
              </p>
            )}
          </section>
        </section>

        <section className="min-w-0 space-y-4">
          <DailyTotals dateKey={selectedDate} totals={totals} copy={historyCopy} foodsCopy={foodsCopy} />

          <section className="rounded-lg border border-border bg-card p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ClipboardList className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold">{historyCopy.foodItems}</h2>
                <p className="text-sm text-muted-foreground">{formatCopy(historyCopy.showingSnapshots, { date: selectedDate })}</p>
              </div>
            </div>

            <Input
              className="mb-4"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={historyCopy.searchPlaceholder}
            />

            {selectedLogs.length > 0 ? (
              <div className="space-y-3">
                {selectedLogs.map((log) => (
                  <LogCard key={log.id} log={log} copy={historyCopy} foodsCopy={foodsCopy} onDelete={handleDelete} />
                ))}
              </div>
            ) : (
              <p className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                {loadingHistory ? historyCopy.loading : historyCopy.noFoodForDate}
              </p>
            )}
          </section>
        </section>
      </div>
    </section>
  );
}

function DailyTotals({ dateKey, totals, copy, foodsCopy }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-bold">{copy.dailyTotals}</h2>
          <p className="text-sm text-muted-foreground">{dateKey}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Nutrient label={foodsCopy.potassiumMg.replace(" (mg)", "")} value={totals.potassium_mg} unit="mg" />
        <Nutrient label={foodsCopy.phosphorusMg.replace(" (mg)", "")} value={totals.phosphorus_mg} unit="mg" />
        <Nutrient label={foodsCopy.sodiumMg.replace(" (mg)", "")} value={totals.sodium_mg} unit="mg" />
        <Nutrient label={foodsCopy.proteinG.replace(" (g)", "")} value={totals.protein_g} unit="g" />
      </div>
    </section>
  );
}

function LogCard({ log, copy, foodsCopy, onDelete }) {
  const portions = getLogPortions(log);
  const listedServing = getLogListedServing(log);

  return (
    <article className="rounded-lg border border-border bg-background p-4">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="break-words text-base font-extrabold">{log.food_name_snapshot}</h3>
          <p className="text-sm text-muted-foreground">
            {portions > 0
              ? `${copy.portionsEaten}: ${formatAmount(portions)}`
              : formatServing(log.quantity, log.quantity_unit)}
            {" · "}
            {formatLogTime(log.created_at)}
          </p>
          {listedServing && (
            <p className="text-xs text-muted-foreground">
              {copy.listedServing}: {formatServing(listedServing.quantity, listedServing.unit)}
            </p>
          )}
        </div>
        <Button type="button" variant="ghost" size="sm" className="shrink-0 self-start text-destructive" onClick={() => onDelete(log)}>
          <Trash2 className="h-4 w-4" />
          {copy.delete}
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Nutrient label={foodsCopy.potassiumMg.replace(" (mg)", "")} value={log.potassium_mg_snapshot} unit="mg" />
        <Nutrient label={foodsCopy.phosphorusMg.replace(" (mg)", "")} value={log.phosphorus_mg_snapshot} unit="mg" />
        <Nutrient label={foodsCopy.sodiumMg.replace(" (mg)", "")} value={log.sodium_mg_snapshot} unit="mg" />
        <Nutrient label={foodsCopy.proteinG.replace(" (g)", "")} value={log.protein_g_snapshot} unit="g" />
      </div>
    </article>
  );
}

function Nutrient({ label, value, unit }) {
  return (
    <div className="rounded-lg bg-muted px-3 py-2 text-sm">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <span className="font-bold">
        {formatAmount(value)} {unit}
      </span>
    </div>
  );
}

function formatCopy(template = "", values = {}) {
  return Object.entries(values).reduce((result, [key, value]) => result.replace(`{${key}}`, value), template);
}

function formatAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(1);
}
